// client/src/App.js
import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import CssBaseline from '@mui/material/CssBaseline';
import Box from '@mui/material/Box';

// Context providers
import { AuthProvider } from './contexts/AuthContext';
import { LocaleProvider } from './contexts/LocaleContext';

// Components
import Navbar from './components/common/Navbar';
import ProtectedRoute from './components/common/ProtectedRoute';
import Login from './components/auth/Login';
import Dashboard from './components/dashboard/dashboard';
import FacultyList from './components/faculty/FacultyList';
import FacultyForm from './components/faculty/FacultyForm';
import UserList from './components/users/UserList';
import UserForm from './components/users/UserForm';
import NotFound from './components/common/NotFound';
import Unauthorized from './components/common/Unauthorized';
import BankAccountList from './components/bankAccounts/BankAccountList';
import BankAccountForm from './components/bankAccounts/BankAccountForm';
import TransactionList from './components/billingTransactions/TransactionList';
import TransactionForm from './components/billingTransactions/TransactionForm';

import BillingExecution from './components/billingExecution/BillingExecution';
import BillingReceiptsList from './components/billingExecution/BillingReceiptsList';
import BillingPreview from './components/billingExecution/BillingPreview';
import BillingExecutionsList from './components/billingExecution/BillingExecutionsList';

import PaymentItemList from './components/paymentItems/PaymentItemList';
import PaymentItemForm from './components/paymentItems/PaymentItemForm';
import PaymentBylawList from './components/paymentBylaws/PaymentBylawList';
import PaymentBylawForm from './components/paymentBylaws/PaymentBylawForm';
import BylawTransactionList from './components/billingTransactions/BylawTransactionList';
import TransactionDetails from './components/billingTransactions/TransactionDetails';

import BillingDetailList from './components/billingDetails/BillingDetailList';
import BillingDetailForm from './components/billingDetails/BillingDetailForm';

import BillingReceiptReview from './components/billingExecution/BillingReceiptReview';

import PaymentUploadsList from './components/paymentUploads/PaymentUploadsList';
import PaymentUploadForm from './components/paymentUploads/PaymentUploadForm';
import PaymentUploadDetails from './components/paymentUploads/PaymentUploadDetails';

import PaymentReports from './components/paymentReports/PaymentReports';


// Import i18n configuration
import './utils/i18n';
//import BankAccount from '../../server/models/BankAccount';

function App() {
  return (
    <LocaleProvider>
      <AuthProvider>
        <Router>
          <CssBaseline />
          <Routes>
            {/* Public routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/unauthorized" element={<Unauthorized />} />
            
            {/* Default redirect to login for root path */}
            <Route path="/" element={<Navigate to="/login" replace />} />
            
            {/* Protected routes */}
            <Route element={<ProtectedRoute />}>
              <Route path="/dashboard" element={
                <>
                  <Navbar />
                  <Box component="main" sx={{ flexGrow: 1, py: 3 }}>
                    <Dashboard />
                  </Box>
                </>
              } />
              
              <Route path="/faculties" element={
                <>
                  <Navbar />
                  <Box component="main" sx={{ flexGrow: 1, py: 3 }}>
                    <FacultyList />
                  </Box>
                </>
              } />

              <Route path="/faculties/create" element={
                <>
                  <Navbar />
                  <Box component="main" sx={{ flexGrow: 1, py: 3 }}>
                    <FacultyForm />
                  </Box>
                </>
              } />

            <Route path="/faculties/edit/:id" element={
                <>
                  <Navbar />
                  <Box component="main" sx={{ flexGrow: 1, py: 3 }}>
                    <FacultyForm />
                  </Box>
                </>
              } />

              <Route path="/users" element={
                <>
                  <Navbar />
                  <Box component="main" sx={{ flexGrow: 1, py: 3 }}>
                    <UserList />
                  </Box>
                </>
              } />              


              <Route path="/users/create" element={
                <>
                  <Navbar />
                  <Box component="main" sx={{ flexGrow: 1, py: 3 }}>
                    <UserForm />
                  </Box>
                </>
              } />              

            <Route path="/users/edit/:id" element={
                <>
                  <Navbar />
                  <Box component="main" sx={{ flexGrow: 1, py: 3 }}>
                    <UserForm />
                  </Box>
                </>
              } />


             <Route path="/bank-accounts" element={
                <>
                  <Navbar />
                  <Box component="main" sx={{ flexGrow: 1, py: 3 }}>
                    <BankAccountList />
                  </Box>
                </>
              } />              


              <Route path="/bank-accounts/create" element={
                <>
                  <Navbar />
                  <Box component="main" sx={{ flexGrow: 1, py: 3 }}>
                    <BankAccountForm />
                  </Box>
                </>
              } />              

            <Route path="/bank-accounts/edit/:id" element={
                <>
                  <Navbar />
                  <Box component="main" sx={{ flexGrow: 1, py: 3 }}>
                    <BankAccountForm />
                  </Box>
                </>
              } />


            <Route path="/billing-transactions" element={
                <>
                  <Navbar />
                  <Box component="main" sx={{ flexGrow: 1, py: 3 }}>
                    <TransactionList />
                  </Box>
                </>
              } />              


              <Route path="/billing-transactions/create" element={
                <>
                  <Navbar />
                  <Box component="main" sx={{ flexGrow: 1, py: 3 }}>
                    <TransactionForm />
                  </Box>
                </>
              } />              

            <Route path="/billing-transactions/edit/:id" element={
                <>
                  <Navbar />
                  <Box component="main" sx={{ flexGrow: 1, py: 3 }}>
                    <TransactionForm />
                  </Box>
                </>
              } />

            <Route path="/billing-receipts" element={
                <>
                  <Navbar />
                  <Box component="main" sx={{ flexGrow: 1, py: 3 }}>
                    <BillingReceiptsList />
                  </Box>
                </>
              } />   

            <Route path="billing-receipts/preview/:id" element={
                <>
                  <Navbar />
                  <Box component="main" sx={{ flexGrow: 1, py: 3 }}>
                    <BillingPreview />
                  </Box>
                </>
              } />   

            <Route path="billing-receipts/review/:id" element={
                <>
                  <Navbar />
                  <Box component="main" sx={{ flexGrow: 1, py: 3 }}>
                    <BillingReceiptReview />
                  </Box>
                </>
              } /> 

          <Route path="billing-execution/preview/:id" element={
                <>
                  <Navbar />
                  <Box component="main" sx={{ flexGrow: 1, py: 3 }}>
                    <BillingPreview />
                  </Box>
                </>
              } />

            <Route path="/billing-execution" element={
                <>
                  <Navbar />
                  <Box component="main" sx={{ flexGrow: 1, py: 3 }}>
                    <BillingExecution />
                  </Box>
                </>
              } />  
  
            <Route path="/billing-execution-list" element={
                <>
                  <Navbar />
                  <Box component="main" sx={{ flexGrow: 1, py: 3 }}>
                    <BillingExecutionsList />
                  </Box>
                </>
              } />  

<Route path="/payment-items" element={
  <>
    <Navbar />
    <Box component="main" sx={{ flexGrow: 1, py: 3 }}>
      <PaymentItemList />
    </Box>
  </>
} />

<Route path="/payment-items/create" element={
  <>
    <Navbar />
    <Box component="main" sx={{ flexGrow: 1, py: 3 }}>
      <PaymentItemForm />
    </Box>
  </>
} />

<Route path="/payment-items/edit/:id" element={
  <>
    <Navbar />
    <Box component="main" sx={{ flexGrow: 1, py: 3 }}>
      <PaymentItemForm />
    </Box>
  </>
} />


<Route path="/billing-details" element={
  <>
    <Navbar />
    <Box component="main" sx={{ flexGrow: 1, py: 3 }}>
      <BillingDetailList />
    </Box>
  </>
} />

<Route path="/billing-details/create" element={
  <>
    <Navbar />
    <Box component="main" sx={{ flexGrow: 1, py: 3 }}>
      <BillingDetailForm />
    </Box>
  </>
} />

<Route path="/billing-details/edit/:id" element={
  <>
    <Navbar />
    <Box component="main" sx={{ flexGrow: 1, py: 3 }}>
      <BillingDetailForm />
    </Box>
  </>
} />



<Route path="/payment-bylaws" element={
  <>
    <Navbar />
    <Box component="main" sx={{ flexGrow: 1, py: 3 }}>
      <PaymentBylawList />
    </Box>
  </>
} />

<Route path="/payment-bylaws/create" element={
  <>
    <Navbar />
    <Box component="main" sx={{ flexGrow: 1, py: 3 }}>
      <PaymentBylawForm />
    </Box>
  </>
} />

<Route path="/payment-bylaws/edit/:id" element={
  <>
    <Navbar />
    <Box component="main" sx={{ flexGrow: 1, py: 3 }}>
      <PaymentBylawForm />
    </Box>
  </>
} />


<Route path="/payment-bylaws/:id/transactions" element={
  <>
    <Navbar />
    <Box component="main" sx={{ flexGrow: 1, py: 3 }}>
      <BylawTransactionList />
    </Box>
  </>
} />

<Route path="/payment-bylaws/:id/transactions/create" element={
  <>
    <Navbar />
    <Box component="main" sx={{ flexGrow: 1, py: 3 }}>
      <TransactionForm />
    </Box>
  </>
} />

<Route path="/payment-bylaws/:id/transactions/edit/:transactionId" element={
  <>
    <Navbar />
    <Box component="main" sx={{ flexGrow: 1, py: 3 }}>
      <TransactionForm />
    </Box>
  </>
} />

<Route path="/payment-bylaws/:id/transactions/:transactionId" element={
  <>
    <Navbar />
    <Box component="main" sx={{ flexGrow: 1, py: 3 }}>
      <TransactionDetails />
    </Box>
  </>
} />


<Route path="/payment-uploads" element={
  <>
    <Navbar />
    <Box component="main" sx={{ flexGrow: 1, py: 3 }}>
      <PaymentUploadsList />
    </Box>
  </>
} />

<Route path="/payment-uploads/create" element={
  <>
    <Navbar />
    <Box component="main" sx={{ flexGrow: 1, py: 3 }}>
      <PaymentUploadForm />
    </Box>
  </>
} />

<Route path="/payment-uploads/:id" element={
  <>
    <Navbar />
    <Box component="main" sx={{ flexGrow: 1, py: 3 }}>
      <PaymentUploadDetails />
    </Box>
  </>
} />


<Route path="/payment-reports" element={
  <>
    <Navbar />
    <Box component="main" sx={{ flexGrow: 1, py: 3 }}>
      <PaymentReports />
    </Box>
  </>
} />



              {/* Add similar patterns for other protected routes */}
            </Route>
            
            {/* 404 route - must be last */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Router>
      </AuthProvider>
    </LocaleProvider>
  );
}

export default App;